<template>

    <!-- Aufbau nach Layoutskizze B: oben Titel und Lernzielband, links die
         Arbeitsflaeche mit dem Geraet, rechts die Spalte (Ablauf, Aufgaben
         oder Themen). Gleich in allen Modi. -->
    <div class="page">

        <header class="kopf">
            <div class="kopf__text">
                <p class="kopf__pfad">{{ currentMode.features.sitzung ? 'Sitzung' : 'Funkgerät' }}</p>
                <h1 class="kopf__titel">{{ currentMode.label }}</h1>
            </div>
            <div class="kopf__knoepfe">
                <button @click="startTour" :class="{'tour-highlight' : isTourTarget('startTour')}">Rundgang starten</button>
            </div>

            <!-- Lernzielband: was hier geuebt wird, rechts der Stand. -->
            <div class="kopf__ziel">
                <div>
                    <p class="kopf__etikett">Lernziel</p>
                    <p class="kopf__lernziel">{{ currentMode.lernziel }}</p>
                </div>
                <p v-if="currentMode.features.showTasks && taskList.length" class="kopf__stand">
                    {{ taskList.filter(t => t.done).length }} von {{ taskList.length }} Aufgaben erledigt
                </p>
                <p v-else-if="currentMode.features.guideAvailable && guideActive" class="kopf__stand">
                    Schritt {{ guideStepIndex + 1 }} von {{ guideSteps.length }}
                </p>
            </div>
        </header>

        <!-- Arbeitsflaeche: das Geraet mittig auf einer Unterlage. Bildet
             keinen Stapelkontext (kein z-index/transform/filter), damit
             Rundgang und Erklaerung das Geraet ueber die Abdunklung heben. -->
        <div class="radio-panel">

            <!-- Lernmodus: Schrittnummer und die eine Anweisung, gross ueber dem Geraet. -->
            <div v-if="currentMode.features.guideAvailable" class="schritt-kopf" aria-live="polite">
                <template v-if="guideActive">
                    <p class="schritt-kopf__nr">Schritt {{ guideStepIndex + 1 }}</p>
                    <p class="schritt-kopf__anweisung">{{ currentGuideStep.label }}</p>
                    <p class="schritt-kopf__hinweis">Mach es am Gerät, dann geht es von selbst weiter.</p>
                </template>
                <template v-else>
                    <p class="schritt-kopf__nr">Anleitung</p>
                    <p class="schritt-kopf__anweisung">
                        {{ guideStepIndex >= guideSteps.length ? 'Alle Schritte erledigt.' : `${guideSteps.length} Schritte durch die Bedienung.` }}
                    </p>
                    <button class="knopf knopf--haupt" @click="startGuide">
                        {{ guideStepIndex >= guideSteps.length ? 'Nochmals von vorn' : 'Anleitung starten' }}
                    </button>
                </template>
            </div>

            <div class="device" :class="{'tour-highlight' : isTourTarget('funkgeraet')}">
                <div class="chassis">
                    <button class="power-btn" @pointerdown="startpowerOff" @pointerup="cancelPowerOff" @pointercancel="cancelPowerOff" :class="{'guide-highlight' : isGuideTarget('power'), spot: isErklaerungTarget('power')}">
                        <svg class="power-btn__ic" viewBox="0 0 24 24">
                            <path v-for="(d, i) in icons.power" :key="i" :d="d" />
                        </svg>
                    </button>
                    <div class="volume-knob" :style="{ '--vol': radio.volume }" :class="{'guide-highlight' : isGuideTarget('volume'), spot: isErklaerungTarget('volume')}">
                        <button class="volume-knob__half volume-knob__half--left" @click="decreaseVolume" aria-label="Leiser">−</button>
                        <button class="volume-knob__half volume-knob__half--right" @click="increaseVolume" aria-label="Lauter">+</button>
                    </div>
                    <Display @key="handleKeyPress"
                    @mode="setPendingMode" 
                    @up="decreaseListIndex" 
                    @down="increaseListIndex" 
                    @confirm="confirmSelection"
                    @exitChannel = "exitChannel"
                        :isOn="radio.isOn"
                        :channel="radio.channel"
                        :volume="radio.volume"
                        :battery="radio.battery"
                        :isTransmitting="radio.isTransmitting"
                        :commMode = "radio.commMode"
                        :screen  = "screen"
                        :listIndex = "listIndex"
                        :currentList = "currentList"
                        :pendingMode = "pendingMode"
                        :highlight = "erklaerungHighlight"
                        :guideTarget = "guideTarget"
                    />
                </div>

                <button
                    class="ptt"
                    :class="{ active: radio.isTransmitting, spot: isErklaerungTarget('ptt'), 'guide-highlight': isGuideTarget('ptt') }"
                    @pointerup="stopTransmit"
                    @pointerdown="startTransmit"
                    @pointercancel="stopTransmit"
                >PTT</button>
            </div>
            <!-- Ueber dem leeren Platz neben der Antenne, ausserhalb des Flusses:
                 der Rundgang verschiebt nichts. -->
            <div v-if="tourActive" class="tour-platz">
                <TourPopUp :tourStep="currentTourStep" @skip="skipTourStep" @close="closeTour"/>
            </div>
        </div>

        <!-- Seitenspalte: alles, was zu lesen ist, steht hier an einem festen
             Ort — Anleitung, Erklaerung, Aufgaben. Am Geraet bleibt nur die
             Markierung, nichts verdeckt es. Waehrend einer Erklaerung liegt
             die Spalte ueber der Abdunklung (ueber-abdunklung). -->
        <div class="task-panel"
             :class="{'tour-highlight' : isTourTarget('taskpanel'), 'ueber-abdunklung': erklaerungActive}">

            <section v-if="currentMode.features.guideAvailable" class="abschnitt">
                <h2>Ablauf</h2>
                <GuidePopUp :schritte="guideSteps" :index="guideStepIndex" :aktiv="guideActive"/>
                <!-- Fusszeile des Ablaufs: in der Spalte statt unter dem Geraet,
                     damit sie nie Tasten verdeckt. -->
                <div v-if="guideActive" class="schritt-fuss">
                    <button @click="skipGuideStep">Überspringen</button>
                    <button @click="closeGuide">Schliessen</button>
                </div>
            </section>

            <template v-if="currentMode.features.showErklaerungen">
                <ErklaerungPopUp v-if="erklaerungActive"
                    :titel="erklaerungTitel"
                    :schritt="currentErklaerungSchritt"
                    :index="erklaerungIndex"
                    :anzahl="erklaerungSchritte.length"
                    :kannWeiter="kannWeiter"
                    @zurueck="erklaerungZurueck"
                    @weiter="erklaerungWeiter"
                    @schliessen="erklaerungSchliessen"/>
                <p v-if="!erklaerungActive" class="abschnitt__hinweis">
                    Wähle ein Thema. Am Gerät siehst du die passende Stelle.
                </p>

                <section class="abschnitt">
                    <h2>Teile des Geräts</h2>
                    <ul class="themen">
                        <li v-for="t in teile" :key="t.id">
                            <button :aria-current="erklaerungActive && erklaerungTitel === t.titel ? 'true' : null"
                                    @click="starteErklaerung(t, teile)">{{ t.titel }}</button>
                        </li>
                    </ul>
                </section>

                <section class="abschnitt">
                    <h2>Abläufe</h2>
                    <ul class="themen">
                        <li v-for="a in ablaeufe" :key="a.id">
                            <button :aria-current="erklaerungActive && erklaerungTitel === a.titel ? 'true' : null"
                                    @click="starteErklaerung(a, ablaeufe)">{{ a.titel }}</button>
                        </li>
                    </ul>
                </section>
            </template>

            <section v-else-if="currentMode.features.showTasks" class="abschnitt">
                <h2>Aufgaben</h2>
                <p v-if="currentMode.features.sitzung && schuelerSitzung.beendet" class="sitzung-beendet" role="status">
                    Die Sitzung ist beendet.
                </p>
                <TaskPanel :tasks="taskList" :filterbar="!currentMode.features.sitzung"/>
            </section>

        </div>

        <div v-if="tourActive || erklaerungActive" class="tour-overlay"></div>
    </div>

</template>

<script>
import { reactive, onUnmounted, ref, computed, watchEffect, onMounted, watch } from 'vue';
import Display from '@/components/Display.vue';
import { kanaele } from '@/data/Kanaele.js';
import { icons } from '@/data/Icons.js';
import TaskPanel from '@/components/TaskPanel.vue';
import { tasks } from '@/data/Tasks';
import { checkers } from '@/logic/checkers';
import { guideSteps } from '@/data/GuideSteps';
import GuidePopUp from '@/components/GuidePopUp.vue';
import { tourSteps } from '@/data/TourSteps';
import TourPopUp from '@/components/TourPopUp.vue';
import { modes } from '@/data/Modes';
import { teile, ablaeufe } from '@/data/Erklaerungen';
import ErklaerungPopUp from '@/components/ErklaerungPopUp.vue';
import { schuelerSitzung, aufgabenFesthalten, standMelden } from '@/logic/sitzungSchueler';
import { ziehenFunk } from '@/logic/sitzungAufgaben';


export default {
    components: {Display, TaskPanel, GuidePopUp, TourPopUp, ErklaerungPopUp},
    props: ['mode'],
    setup(props) {
        let batteryInterval = null
        const keyBuffer = ref([])
        let pendingMode = ref('')
        let screen = ref('')
        const listIndex = ref(0)
        let powerTimer = null
        const guideActive = ref(false)
        const guideStepIndex = ref(0)
        const isGuideTarget = (name) => guideActive.value && currentGuideStep.value?.target === name
        // Dasselbe als Name statt als Frage, fuer Display.vue: dort liegen
        // Tastatur, Auswahltasten und Auflegen-Taste, auf die der Guide
        // bisher gar nicht zeigen konnte.
        const guideTarget = computed(() => guideActive.value ? (currentGuideStep.value?.target ?? '') : '')
        const isTourTarget = (name) => tourActive.value && currentTourStep.value?.target === name
        const tourActive = ref(false)
        const tourStepIndex = ref(0)

        // Erklaerungen (Modus "Geraet kennenlernen"): ein gewaehltes Thema
        // mit seinen Schritten. Eigener Zustand neben Guide und Rundgang,
        // wie der Rundgang seinerzeit neben dem Guide.
        const erklaerungActive = ref(false)
        const erklaerungTitel = ref('')
        const erklaerungSchritte = ref([])
        const erklaerungIndex = ref(0)
        // Die Gruppe, aus der das Thema stammt (teile oder ablaeufe), und die
        // Stelle darin — damit "Weiter" am Themenende weitergehen kann.
        const erklaerungGruppe = ref([])
        const erklaerungThemaIndex = ref(0)
        // Weiter gibt es, solange im Thema noch ein Schritt folgt oder in der
        // Gruppe noch ein Thema. Sonst zeigt die Blase keinen Weiter-Knopf.
        const kannWeiter = computed(() =>
            erklaerungIndex.value < erklaerungSchritte.value.length - 1 ||
            !!erklaerungGruppe.value[erklaerungThemaIndex.value + 1]
        )
        const currentErklaerungSchritt = computed(() => erklaerungSchritte.value[erklaerungIndex.value])

        // `target` darf ein Name oder eine Liste von Namen sein. Hier wird
        // daraus immer eine Liste, damit der Rest nur einen Fall kennt.
        const zieleVonSchritt = (schritt) => {
            if (!schritt?.target) return []
            return Array.isArray(schritt.target) ? schritt.target : [schritt.target]
        }

        // Faellt als Prop in Display.vue, weil die halbe Geraeteoberflaeche
        // dort drin liegt und Display nichts von Erklaerungen weiss.
        const erklaerungHighlight = computed(() => erklaerungActive.value ? zieleVonSchritt(currentErklaerungSchritt.value) : [])
        const isErklaerungTarget = (name) => erklaerungHighlight.value.includes(name)

        const radio = reactive({
            isOn: false,
            channel: null,
            volume: 1,
            battery: 100,
            isTransmitting: false,
            commMode: '',
            hasBeenOn: false,
            hasHadChannel: false,
            // Merkflags fuer den Lernmodus. Wie hasBeenOn/hasHadChannel
            // halten sie fest, WAS schon einmal getan wurde — der blosse
            // Zustand verraet den Weg nicht: ein ueber die Liste gewaehlter
            // Kanal sieht aus wie ein getippter, und ein Schritt zurueck im
            // Menue hinterlaesst gar nichts. Ein Aus- und Einschalten setzt
            // sie bewusst NICHT zurueck, genau wie bei hasBeenOn.
            hasUsedTastatur: false,
            hasUsedListe: false,
            hasGoneBack: false,
            hasTransmitted: false
        })

        const togglePower = () => {
            radio.isOn = !radio.isOn
            if (radio.isOn) radio.hasBeenOn = true
            if (!radio.isOn) stopTransmit()
            radio.battery = 100
            screen.value = ''
            keyBuffer.value = []
            pendingMode.value = ''
        }

        const setChannel = (i) => {
            if(!radio.isOn) return
            radio.channel = i
            radio.hasHadChannel = true
        }

        const startTransmit = () => {
            if (!radio.isOn) return
            radio.isTransmitting = true
            if (radio.channel !== null) radio.hasTransmitted = true
            console.log('Übertragung gestartet')

            batteryInterval = setInterval(() => {
                radio.battery -= 1
                if (radio.battery <= 0){
                    stopTransmit()
                }
            }, 500)
        }

        const stopTransmit = () => {
            radio.isTransmitting = false
            console.log('Übertragung gestoppt')
            clearInterval(batteryInterval)
        }


        const increaseVolume = () => {
            if (!radio.isOn) return
            radio.volume = Math.min(7, radio.volume + 1)
        }

        const decreaseVolume = () => {
            if (!radio.isOn) return
            radio.volume = Math.max(1, radio.volume - 1)
        }

        const handleKeyPress = (key) => {
            if (!pendingMode.value || screen.value !== 'input') return
            if (key >= '0' && key <= '9'){
                keyBuffer.value.push(key)
                console.log(keyBuffer)
            }

            if (keyBuffer.value.length === 4) {
                const channelNumber = keyBuffer.value.join('')
                console.log(channelNumber)
                setChannel(channelNumber)
                setCommMode(pendingMode.value)
                radio.hasUsedTastatur = true
                keyBuffer.value = []
                pendingMode.value = ''
                screen.value = ''
            }
        }

        const setPendingMode = (tempmode) => {
            if (!radio.isOn) return
            if (screen.value === 'confirmOff') {
                if (tempmode === 'G') {
                    screen.value = ''
                    return
                }else if (tempmode === 'D'){
                    togglePower()
                    return
                }
            }
            if (!pendingMode.value) {
                pendingMode.value = tempmode
                screen.value = 'input'
            } else if (tempmode === 'G') {
                screen.value = 'list'
                listIndex.value = 0
            } else {
                pendingMode.value = ''
                screen.value = ''
                keyBuffer.value = []
                radio.hasGoneBack = true
            }
        }

        const setCommMode  = (mode) => {
            if (!radio.isOn) return
            radio.commMode = mode
        }

        

        const increaseListIndex = () => {
            if (screen.value !== 'list') return
                listIndex.value = Math.min(currentList.value.length -1, listIndex.value +1)
                console.log(listIndex.value, currentList.value)
        }

        const decreaseListIndex = () => {
            if (screen.value !== 'list') return
                listIndex.value = Math.max(0, listIndex.value -1)
                console.log(listIndex.value, currentList.value)
        }

        const confirmSelection = () => {
            if (screen.value !== 'list') return
            const selected = currentList.value[listIndex.value]
            setChannel(selected.number)
            setCommMode(pendingMode.value)
            radio.hasUsedListe = true
            pendingMode.value = ''
            screen.value = ''
            keyBuffer.value = []
            listIndex.value = 0
        }

        const exitChannel = () => {
            if (!radio.isOn || screen.value === 'confirmOff') return
            radio.channel = null
            radio.commMode = ''
        }

        const startpowerOff = () => {
            if (!radio.isOn) {
                togglePower()
                return
            }
            powerTimer = setTimeout(() => {
                screen.value = 'confirmOff'
                pendingMode.value = ''
                keyBuffer.value = []
            }, 2000)
        }

        const cancelPowerOff = () => {
            clearTimeout(powerTimer)
        }

        const currentList = computed(() => kanaele.filter(k => k.mode === pendingMode.value))
        
        
        // Sitzung: die Aufgaben kommen aus der Sitzung, einmal gezogen und
        // gemerkt. Nach einem Neuladen sind erledigte Aufgaben wieder erledigt.
        const imSitzungsModus = modes.find(m => m.id === props.mode).features.sitzung

        const sitzungsAufgaben = () => {
            const d = schuelerSitzung.daten
            if (!d.aufgaben) {
                const { aufgaben, stand } = ziehenFunk(d.konfiguration)
                aufgabenFesthalten(aufgaben, stand)
            }
            return d.aufgaben
                .map(label => tasks.find(t => t.label === label))
                .filter(Boolean)
                .map(t => ({...t, done: d.stand.some(e => e.aufgabe === t.label && e.ergebnis === 'richtig')}))
        }

        const taskList = reactive(
            imSitzungsModus ? sitzungsAufgaben() : tasks.map(t => ({...t, done: false}))
        )

        // Jede neu erledigte Aufgabe geht als ganzer Stand an den Server.
        if (imSitzungsModus) {
            watch(() => taskList.map(t => t.done), () => {
                const stand = schuelerSitzung.daten.stand.map(e => {
                    const t = taskList.find(t => t.label === e.aufgabe)
                    return t && t.done ? {...e, ergebnis: 'richtig'} : e
                })
                standMelden(stand)
            })
        }

        watchEffect(() => {
            for (const t of taskList) {
                if (!t.done && checkers[t.type](radio, t.params)) t.done = true
            }
        })

        const finishOrAdvance = () => {
            guideStepIndex.value += 1
            if (guideStepIndex.value >= guideSteps.length) guideActive.value = false
        }

        const skipGuideStep = () => {
            finishOrAdvance()
        }

        const startGuide = () => {
            guideActive.value = true
            guideStepIndex.value = 0
            tourActive.value = false
        }

        const closeGuide = () => {
            guideActive.value =false
        }

        const currentGuideStep = computed(() => guideSteps[guideStepIndex.value])
        
        watchEffect(() => {
            if (!guideActive.value) return
            while (guideActive.value) {
                const step = currentGuideStep.value
                // Ein Schritt ohne (bekannten) Checker rueckt nie von selbst
                // weiter — sonst wuerde hier undefined aufgerufen.
                if (step && checkers[step.type] && checkers[step.type](radio, step.params)) {
                    finishOrAdvance()
                } else {
                    break
                }
            }
        })

        const currentTourStep = computed(() => tourSteps[tourStepIndex.value])

        const finishOrAdvanceTour = () => {
            tourStepIndex.value += 1
            if (tourStepIndex.value >= tourSteps.length) tourActive.value = false
        }

        const skipTourStep = () => {
            finishOrAdvanceTour()
        }

        const startTour = () => {
            tourActive.value = true
            tourStepIndex.value = 0
            guideActive.value = false
        }

        const closeTour = () => {
            tourActive.value =false
        }

        // Ein Schritt darf einen Geraetezustand stellen, damit das Display
        // wirklich zeigt, wovon der Text spricht. Dafuer wird beim Start
        // einmal der echte Zustand weggelegt und beim Schliessen
        // zurueckgeschrieben — eine Erklaerung ist eine Vorfuehrung und
        // soll nichts hinterlassen.
        let gemerkterZustand = null

        const zustandMerken = () => {
            gemerkterZustand = {
                isOn: radio.isOn,
                channel: radio.channel,
                volume: radio.volume,
                battery: radio.battery,
                isTransmitting: radio.isTransmitting,
                commMode: radio.commMode,
                screen: screen.value,
                pendingMode: pendingMode.value,
                listIndex: listIndex.value,
                keyBuffer: [...keyBuffer.value]
            }
        }

        // Schreibt direkt in radio, ruft also NICHT startTransmit() auf.
        // Genau so soll es sein: ein gestelltes 'T' im Display darf keinen
        // Akku-Intervall starten, das Bild steht still.
        const zustandSetzen = (z) => {
            if (!z) return
            for (const feld of ['isOn', 'channel', 'volume', 'battery', 'isTransmitting', 'commMode']) {
                if (feld in z) radio[feld] = z[feld]
            }
            if ('screen' in z) screen.value = z.screen
            if ('pendingMode' in z) pendingMode.value = z.pendingMode
            if ('listIndex' in z) listIndex.value = z.listIndex
        }

        const zustandWiederherstellen = () => {
            if (!gemerkterZustand) return
            zustandSetzen(gemerkterZustand)
            keyBuffer.value = [...gemerkterZustand.keyBuffer]
        }

        // Jeder Schrittwechsel geht vom echten Zustand aus und stellt erst
        // dann neu. Sonst bliebe ein 'T' aus Schritt 2 in Schritt 3 stehen.
        // Bewusst von Hand aufgerufen statt per watcher: der Schritt kann
        // sich auf dasselbe Objekt zurueckaendern (gleiches Thema erneut
        // geoeffnet), und ein watch wuerde dann nicht ausloesen.
        const schrittAnzeigen = () => {
            zustandWiederherstellen()
            zustandSetzen(currentErklaerungSchritt.value?.zustand)
        }

        const erklaerungSchliessen = () => {
            zustandWiederherstellen()
            gemerkterZustand = null
            erklaerungActive.value = false
            erklaerungIndex.value = 0
        }

        // Setzt den Index immer auf 0 zurueck — sonst startet ein zweiter
        // Aufruf hinter dem Ende und die Blase waere leer. Gleicher Fehler
        // wie seinerzeit in startGuide().
        const themaLaden = (eintrag) => {
            erklaerungTitel.value = eintrag.titel
            erklaerungSchritte.value = eintrag.schritte
            erklaerungIndex.value = 0
            schrittAnzeigen()
        }

        const starteErklaerung = (eintrag, gruppe) => {
            // Nur merken, wenn noch keine Erklaerung laeuft — sonst wuerde
            // ein bereits gestellter Zustand zum "echten" erklaert und beim
            // Schliessen falsch wiederhergestellt.
            if (!erklaerungActive.value) zustandMerken()
            erklaerungGruppe.value = gruppe
            erklaerungThemaIndex.value = gruppe.indexOf(eintrag)
            erklaerungActive.value = true
            guideActive.value = false
            tourActive.value = false
            themaLaden(eintrag)
        }

        // "Weiter" geht erst durch die Schritte des Themas und danach zum
        // naechsten Thema DERSELBEN Gruppe. Teile und Ablaeufe bleiben damit
        // getrennt, so wie entschieden — es entsteht keine durchgehende Kette
        // vom ersten Teil bis zum letzten Ablauf.
        const erklaerungWeiter = () => {
            if (erklaerungIndex.value < erklaerungSchritte.value.length - 1) {
                erklaerungIndex.value += 1
                schrittAnzeigen()
                return
            }
            const naechstes = erklaerungGruppe.value[erklaerungThemaIndex.value + 1]
            if (!naechstes) return
            erklaerungThemaIndex.value += 1
            themaLaden(naechstes)
        }

        const erklaerungZurueck = () => {
            if (erklaerungIndex.value === 0) return
            erklaerungIndex.value -= 1
            schrittAnzeigen()
        }

        const currentMode = computed(() => modes.find(m => m.id === props.mode))


        onMounted(() => {
            // togglePower() statt radio.isOn = true, damit hasBeenOn und die
            // uebrigen Felder denselben Weg nehmen wie beim echten Einschalten.
            if (currentMode.value.features.deviceStartsOn) togglePower()
            if (currentMode.value.features.guideAutostart) startGuide()
        })

        onUnmounted(() => clearInterval(batteryInterval))



        return {
                icons,
                radio,
                screen,
                currentList,
                listIndex,
                pendingMode,
                togglePower, 
                setChannel, 
                startTransmit, 
                stopTransmit, 
                increaseVolume, 
                decreaseVolume, 
                handleKeyPress, 
                setCommMode, 
                setPendingMode,
                currentList,
                decreaseListIndex,
                increaseListIndex,
                confirmSelection,
                exitChannel,
                startpowerOff,
                cancelPowerOff,
                taskList,
                schuelerSitzung,
                finishOrAdvance,
                currentGuideStep,
                guideSteps,
                guideStepIndex,
                skipGuideStep,
                startGuide,
                guideActive,
                isGuideTarget,
                guideTarget,
                closeGuide,
                finishOrAdvanceTour,
                currentTourStep,
                skipTourStep,
                closeTour,
                startTour,
                isTourTarget,
                tourActive,
                currentMode,
                teile,
                ablaeufe,
                erklaerungActive,
                erklaerungTitel,
                erklaerungSchritte,
                erklaerungIndex,
                currentErklaerungSchritt,
                isErklaerungTarget,
                erklaerungHighlight,
                starteErklaerung,
                kannWeiter,
                erklaerungWeiter,
                erklaerungZurueck,
                erklaerungSchliessen
                }
    }
}

</script>

<style scoped>
/* Layoutskizze B: Arbeitsflaeche nimmt den freien Platz, die Spalte rechts
   hat eine feste Breite. Kein leerer Rand mehr rechts. */
.page {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(320px, 400px);
    justify-content: stretch;
    align-items: start;
    column-gap: 1.5rem;
    row-gap: 1.25rem;
    max-width: 1480px;
    min-height: 100vh;
    margin: 0;
    padding: 2.25rem 2.5rem 3rem;
    box-sizing: border-box;
    background: transparent;
}

/* Die Werkbank: weisse Tafel mit feinem Punkteraster als Unterlage, das
   Geraet mittig. Mindestens so hoch wie der Bildschirm unter dem Kopf. */
.radio-panel {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 0;
    min-height: calc(100vh - 14rem);
    padding: 2rem 1.5rem 2.5rem;
    background-color: var(--zs-tafel);
    background-image: radial-gradient(circle, rgba(19, 26, 38, 0.09) 1px, transparent 1.2px);
    background-size: 22px 22px;
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke-gross);
    box-shadow: var(--zs-schatten);
    box-sizing: border-box;
}

.radio-image {
    width: 180px;
    max-width: 100%;
    filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.2));
}

.device {
    position: relative;
    margin-top: 170px; /* room for the antenna above the chassis */
    /* Das ganze Geraet (mit Antenne rund 900 px) passt in die Hoehe des
       Bildschirms, auch die Zifferntasten. zoom statt transform: zoom
       nimmt auch im Layout weniger Platz und bildet keinen eigenen
       Stapelkontext, das Anheben ueber die Abdunklung geht weiter.
       Display misst innen in px, darum nicht ueber Masse verkleinern. */
    zoom: 0.72;
}

@media (min-height: 950px) {
    .device {
        zoom: 0.85;
    }
}

@media (min-height: 1100px) {
    .device {
        zoom: 1;
    }
}

/* ─── Lernmodus: Schritt und Anweisung ueber dem Geraet ───── */
.schritt-kopf {
    max-width: 34rem;
    margin-bottom: 1.75rem; /* Platz fuer die Antenne darunter */
    padding: 0.85rem 1.25rem;
    text-align: center;
    background: var(--zs-tafel);
    border-radius: var(--zs-ecke);
    box-shadow: var(--zs-schatten);
}

.schritt-kopf__nr {
    margin: 0 0 0.35rem;
    font-size: 0.8rem;
    font-weight: 650;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--zs-grau);
}

.schritt-kopf__anweisung {
    margin: 0 0 0.4rem;
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1.3;
    letter-spacing: -0.01em;
    color: var(--zs-tinte);
}

.schritt-kopf__hinweis {
    margin: 0;
    font-size: 0.95rem;
    color: var(--zs-grau);
}

.schritt-kopf .knopf {
    margin-top: 0.75rem;
}

/* Unten in der Ablaufspalte: ueberspringen und schliessen. Der Stand
   (Schritt n von 9) steht im Lernzielband. */
.schritt-fuss {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    margin: 1rem -0.4rem 0;
    padding-top: 0.75rem;
    border-top: 1px solid var(--zs-rahmen);
}

.schritt-fuss button {
    min-height: 2.5rem;
    padding: 0.4rem 0.75rem;
    font: inherit;
    font-size: 0.95rem;
    color: var(--zs-grau);
    background: transparent;
    border: none;
    border-radius: var(--zs-ecke);
    cursor: pointer;
}

.schritt-fuss button:hover {
    color: var(--zs-tinte);
    background: var(--zs-flaeche);
}

.schritt-fuss button:focus-visible {
    outline: 3px solid var(--zs-orange);
    outline-offset: 2px;
}

.schritt-fuss__zaehler {
    margin-left: auto;
    padding-right: 0.4rem;
    font-size: 0.9rem;
    font-variant-numeric: tabular-nums;
    color: var(--zs-grau);
}

/* Schmal: Geraet und Spalte untereinander, kein seitliches Scrollen. */
@media (max-width: 760px) {
    .page {
        grid-template-columns: minmax(0, 1fr);
        padding: 1rem 16px 2rem;
    }

    .radio-panel {
        min-height: 0;
        padding: 1.25rem 0.75rem 1.5rem;
    }

    .task-panel {
        position: static;
        max-height: none;
        overflow: visible;
    }
}

.chassis {
    position: relative;
    background: rgb(93, 94, 98);
    border-radius: 16px;
    padding: 50px 26px 26px 26px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
}

/* Antenna: ribbed helical whip (ribs on the lower part, smooth rounded tip),
   centred over the power button, sitting on a threaded collar. */
.chassis::before {
    content: '';
    position: absolute;
    top: -166px;
    right: 30px;
    width: 28px;
    height: 170px;
    border-radius: 14px 14px 4px 4px;
    background:
        linear-gradient(90deg, rgba(0, 0, 0, 0.55), rgba(255, 255, 255, 0.12) 35%, rgba(0, 0, 0, 0.55)),
        repeating-linear-gradient(180deg, #2c2e33 0 4px, #141518 4px 6px) bottom / 100% 72% no-repeat,
        #25272b;
    box-shadow: inset 0 0 0 1px #000, 1px 0 4px rgba(0, 0, 0, 0.35);
}

.chassis::after {
    content: '';
    position: absolute;
    top: -14px;
    right: 24px;
    width: 40px;
    height: 16px;
    border-radius: 5px 5px 0 0;
    background:
        repeating-linear-gradient(180deg, transparent 0 3px, rgba(0, 0, 0, 0.45) 3px 4px),
        linear-gradient(90deg, #2a2b2f, #55575d 45%, #2a2b2f);
    box-shadow: inset 0 0 0 1px #000;
}

.power-btn {
    position: absolute;
    top: 7px;
    right: 26px;
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 6px;
    background: #000000;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: inset 0 0 0 1px #000, 0 2px 4px rgba(0, 0, 0, 0.3);
}

.power-btn__ic {
    width: 20px;
    height: 20px;
    fill: none;
    stroke: #ffffff;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
}

/* Knob face with grip ridges; the half-buttons sit invisibly on top. */
.volume-knob {
    position: absolute;
    top: 5px;
    left: 18px;
    width: 72px;
    height: 40px;
    border-radius: 20px;
    display: flex;
}

.volume-knob::before {
    content: '';
    position: absolute;
    top: 0;
    left: 16px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background:
        radial-gradient(circle at 50% 50%, #2b2d31 0 11px, transparent 12px),
        repeating-conic-gradient(#111 0 6deg, #3a3c41 6deg 12deg);
    box-shadow: inset 0 0 0 2px #000, 0 2px 5px rgba(0, 0, 0, 0.5);
    pointer-events: none;
}

/* Position marker: rotates from -135deg (vol 1) to +135deg (vol 7). */
.volume-knob::after {
    content: '';
    position: absolute;
    top: 3px;
    left: 35px;
    width: 2px;
    height: 12px;
    border-radius: 1px;
    background: #ffffff;
    transform-origin: 1px 17px;
    transform: rotate(calc((var(--vol) - 4) * 45deg));
    transition: transform 0.2s ease-out;
    pointer-events: none;
}

.volume-knob__half {
    flex: 1;
    border: none;
    background: transparent;
    color: #cfd2d8;
    font-size: 0.9rem;
    font-weight: 700;
    cursor: pointer;
    padding: 0;
    -webkit-tap-highlight-color: transparent;
}

.volume-knob__half--left {
    text-align: left;
    border-radius: 20px 0 0 20px;
}

.volume-knob__half--right {
    text-align: right;
    border-radius: 0 20px 20px 0;
}

.volume-knob__half:active {
    color: #ffffff;
}

.ptt {
    position: absolute;
    top: 126px;
    right: -39px;
    width: 40px;
    height: 110px;
    border: none;
    border-radius: 0 10px 10px 0;
    background: linear-gradient(180deg, #2a2f37, #1a1d22);
    color: #ccc;
    font-weight: 700;
    font-size: 0.75rem;
    letter-spacing: 0.05em;
    writing-mode: vertical-rl;
    text-orientation: mixed;
    cursor: pointer;
    box-shadow: inset 0 0 0 2px #000, 2px 0 6px rgba(0, 0, 0, 0.4);
    transition: background 0.15s, color 0.15s;
}

.ptt.active {
    background: linear-gradient(180deg, #e05a5a, #c0392b);
    color: #ffffff;
}

/* Bleibt beim Scrollen stehen. sticky bildet einen Stapelkontext: was
   in der Spalte steht, kann nicht einzeln ueber die Abdunklung, nur die
   ganze Spalte (ueber-abdunklung, waehrend einer Erklaerung). */
.task-panel {
    position: sticky;
    top: calc(var(--zs-kopfhoehe) + 1rem);
    background: #ffffff;
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke-gross);
    box-shadow: var(--zs-schatten);
    padding: 1.25rem 1.25rem 1.5rem;
    color: var(--zs-tinte);
    text-align: left;
    /* Nie hoeher als der Bildschirm: eine lange Liste scrollt in der
       Spalte, die Seite bleibt stehen und das Geraet im Blick. */
    max-height: calc(100vh - var(--zs-kopfhoehe) - 2rem);
    overflow-y: auto;
    box-sizing: border-box;
}

.tour-platz {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 102;
}

.task-panel.ueber-abdunklung {
    z-index: 101;
}

/* ─── Abschnitte in der Seitenspalte ─────────────────────────
   Jeder Abschnitt: kleine Ueberschrift, darunter der Inhalt. Zwischen
   zwei Abschnitten eine feine Linie statt eines weiteren Kastens. */
.abschnitt + .abschnitt {
    margin-top: 1.25rem;
    padding-top: 1.25rem;
    border-top: 1px solid var(--zs-rahmen);
}

.task-panel h2 {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    margin: 0 0 0.75rem;
    font-size: 0.75rem;
    font-weight: 650;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--zs-grau);
}

.task-panel h2::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--zs-rahmen);
}

.abschnitt__hinweis {
    margin: 0 0 1rem;
    font-size: 0.95rem;
    line-height: 1.5;
    color: var(--zs-grau);
}

/* Dieselben drei Knopfarten wie in den Blasen: Haupt (blau),
   Neben (weiss mit Linie), Text (ohne Flaeche). */
.knopf {
    min-height: 2.5rem;
    padding: 0.5rem 1rem;
    font: inherit;
    font-size: 0.95rem;
    font-weight: 500;
    border-radius: var(--zs-ecke);
    cursor: pointer;
}

.knopf--haupt {
    color: #ffffff;
    background: var(--zs-blau);
    border: 1px solid var(--zs-blau);
}

.knopf--haupt:hover {
    background: var(--zs-blau-dunkel);
}

.knopf:focus-visible {
    outline: 3px solid var(--zs-orange);
    outline-offset: 2px;
}

.guide-highlight {
    animation: guide-pulse 1s ease-in-out infinite;
}

/* Tour spotlight: overlay dims and blocks the page (z 100), the current
   target is lifted above it (z 101), TourPopUp sits on top (z 102).
   Lifting only works while no ancestor of a target creates its own
   stacking context (z-index, transform, opacity < 1, filter). */
.tour-overlay {
    position: fixed;
    inset: 0;
    z-index: 100;
    background: rgba(31, 35, 40, 0.65); /* Anthrazit */
}

.tour-highlight {
    position: relative;
    z-index: 101;
}

/* Die Spalte bleibt auch angeleuchtet sticky: als relative wuerde ihr
   top-Abstand sie nach unten schieben. z-index wirkt auch an sticky. */
.task-panel.tour-highlight {
    position: sticky;
}

/* Warnrot (--zs-warnrot) as rgba, since the alpha animates. */
@keyframes guide-pulse {
    0%, 100% {
        outline: 4px solid rgba(179, 38, 30, 0.95);
        outline-offset: 2px;
    }
    50% {
        outline: 4px solid rgba(179, 38, 30, 0.2);
        outline-offset: 6px;
    }
}

/* ─── Kopfzeile ───────────────────────────────────────────────
   Nimmt die ganze Breite der .page-Flexzeile, damit Geraet und
   Seitenspalte darunter nebeneinander bleiben. */
.kopf {
    grid-column: 1 / -1;
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 0.75rem 1.5rem;
    text-align: left;
}

/* Pfad und Titel wie auf den Sprechregeln-Seiten. */
.kopf__pfad {
    margin: 0 0 0.35rem;
    font-size: 0.75rem;
    font-weight: 650;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--zs-grau);
}

.kopf__titel {
    margin: 0;
    font-size: 2rem;
    font-weight: 750;
    line-height: 1.15;
    letter-spacing: -0.015em;
}

/* Lernzielband ueber die ganze Breite, unter Titel und Knopf. */
.kopf__ziel {
    display: flex;
    flex: 1 1 100%;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem 2rem;
    margin-top: 0.5rem;
    padding: 0.9rem 1.25rem;
    background: var(--zs-tafel);
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke-gross);
}

.kopf__etikett {
    margin: 0 0 0.15rem;
    font-size: 0.72rem;
    font-weight: 650;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--zs-grau);
}

.kopf__lernziel {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
}

.kopf__stand {
    margin: 0;
    font-size: 0.9rem;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: var(--zs-grau);
}

.kopf__knoepfe {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-left: auto;
}

.kopf button {
    min-height: 2.5rem;
    padding: 0.45rem 1rem;
    font: inherit;
    font-size: 0.9rem;
    background: #ffffff;
    color: var(--zs-tinte);
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke);
    cursor: pointer;
}

.kopf button:hover {
    border-color: var(--zs-orange);
}

.kopf button:focus-visible {
    outline: 3px solid var(--zs-orange);
    outline-offset: 2px;
}

.sitzung-beendet {
    margin: 0 0 1rem;
    padding: 0.6rem 0.8rem;
    border: 1px solid rgba(179, 38, 30, 0.3);
    border-radius: var(--zs-ecke);
    background: #fbeceb;
    color: var(--zs-warnrot);
}

/* ─── Themenliste (Gerät kennenlernen) ───────────────────────
   Eine Liste, keine Knopfsammlung: Zeilen mit Trennlinie, rechts ein
   Pfeil. Das offene Thema traegt aria-current und ist hinterlegt. */
.themen {
    margin: 0;
    padding: 0;
    list-style: none;
}

.themen li + li {
    border-top: 1px solid var(--zs-rahmen);
}

.themen button {
    position: relative;
    display: block;
    width: 100%;
    min-height: 2.5rem;
    padding: 0.55rem 2rem 0.55rem 0.6rem;
    font: inherit;
    font-size: 0.95rem;
    text-align: left;
    color: var(--zs-tinte);
    background: transparent;
    border: none;
    border-radius: var(--zs-ecke);
    cursor: pointer;
}

.themen button::after {
    content: '';
    position: absolute;
    top: 50%;
    right: 0.8rem;
    width: 0.45rem;
    height: 0.45rem;
    border-top: 2px solid var(--zs-grau);
    border-right: 2px solid var(--zs-grau);
    transform: translateY(-50%) rotate(45deg);
}

.themen button:hover {
    background: var(--zs-flaeche);
}

.themen button[aria-current="true"] {
    font-weight: 600;
    color: var(--zs-blau);
    background: var(--zs-blau-hell);
}

.themen button[aria-current="true"]::after {
    border-color: var(--zs-blau);
}

.themen button:focus-visible {
    outline: 3px solid var(--zs-orange);
    outline-offset: -1px;
}

/* Spotlight am Geraet selbst — Gegenstueck zu .spot in Display.vue.
   Dort gibt es zusaetzlich .spot-innen fuer alles im abgeschnittenen
   Displaybereich.
   KEIN position: relative hier: .power-btn, .volume-knob und .ptt sind
   alle position: absolute, und relative wuerde das ueberschreiben — die
   Teile fielen zurueck in den normalen Fluss und ruetschten eine Zeile
   nach unten. z-index wirkt an ihnen ohnehin, sie sind ja positioniert. */
.spot {
    z-index: 101;
    outline: 3px solid var(--zs-orange);
    outline-offset: 3px;
    /* Eingefroren waehrend einer Erklaerung — siehe Display.vue. */
    pointer-events: none;
}

/* ─── Foto statt Zeichnung ───────────────────────────────────
   Das Geraet ist das Foto assets/Funkgeraet.png (294 x 840 px). Alle
   Masse unten sind Pixel im Foto: --x/--y links oben, --b/--h Breite
   und Hoehe; mal --s ergibt Bildschirmpixel. Oben ist das Foto bei --c
   abgeschnitten, damit es samt Zifferntasten in die Hoehe passt; die
   Antenne blendet dort aus. Die Knoepfe liegen unsichtbar auf dem Foto.
   --s und --c stehen an .device, weil .ptt neben .chassis liegt; auch
   Display.vue erbt sie fuer Bildschirm und Tasten. */
.device {
    --s: 1.6;
    --c: 200px;
    margin-top: 0;
}

.chassis {
    width: calc(294px * var(--s));
    height: calc((840px - var(--c)) * var(--s));
    padding: 0;
    border-radius: 0;
    box-shadow: none;
    background:
        linear-gradient(var(--zs-tafel), transparent) calc(200px * var(--s)) 0 / calc(80px * var(--s)) calc(50px * var(--s)) no-repeat,
        url('~@/assets/Funkgeraet.png') 0 calc(var(--c) * -1 * var(--s)) / calc(294px * var(--s)) auto no-repeat;
}

.chassis::before,
.chassis::after,
.volume-knob::before,
.volume-knob::after {
    content: none;
}

.power-btn,
.volume-knob,
.ptt {
    left: calc(var(--x) * var(--s) * 1px);
    right: auto;
    top: calc((var(--y) * 1px - var(--c)) * var(--s));
    width: calc(var(--b) * var(--s) * 1px);
    height: calc(var(--h) * var(--s) * 1px);
    background: transparent;
    box-shadow: none;
}

/* Kleiner runder Knopf oben rechts neben der Antenne. */
.power-btn {
    --x: 218; --y: 318; --b: 32; --h: 32;
    border-radius: 50%;
}

.power-btn__ic {
    visibility: hidden;
}

/* Drehknopf oben links: linke Haelfte leiser, rechte lauter. */
.volume-knob {
    --x: 76; --y: 270; --b: 72; --h: 36;
}

.volume-knob__half {
    color: transparent;
}

/* Lange Taste an der linken Seite. */
.ptt {
    --x: 38; --y: 424; --b: 20; --h: 96;
    border-radius: 6px;
    color: transparent;
}

.power-btn:active,
.volume-knob__half:active {
    background: rgba(0, 0, 0, 0.3);
}

.ptt.active {
    background: rgba(224, 90, 90, 0.55);
    color: transparent;
}
</style>
